import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import { useCRM } from '../../context/CRMContext';
import { useTenant } from '../../context/TenantContext';
import CommandPalette from '../common/CommandPalette';
import { getRolePermissions } from '../../utils/rolePermissions';

const Header = ({ onOpenSidebar }) => {
  const { currentUser } = useAuth();
  const { notifications, toggleHelp, darkMode, toggleDarkMode } = useCRM();
  const { activeMembership, activeTenant } = useTenant();
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const permissions = getRolePermissions(currentUser, activeMembership);
  const tenantName = activeTenant?.name || 'Sales Work';

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <header className="h-20 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 px-8 flex items-center justify-between sticky top-0 z-40 transition-colors">

        <div className="flex items-center gap-4 flex-1 max-w-xl">
          <button
            type="button"
            onClick={onOpenSidebar}
            aria-label="Åpne meny"
            className="lg:hidden p-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl transition-all"
          >
            <SafeIcon icon="☰" className="w-5 h-5" />
          </button>

          <button
            onClick={() => setIsPaletteOpen(true)}
            className="w-full flex items-center gap-3 px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all group"
          >
            <SafeIcon icon="🔍" className="w-4 h-4" />
            <span className="text-sm font-medium flex-1 text-left dark:text-slate-500">Søk etter kontakter eller firmaer...</span>
            <div className="flex items-center gap-1 px-1.5 py-0.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded text-[10px] font-black text-slate-400 group-hover:border-slate-300">
              <SafeIcon icon="⌘" className="w-2.5 h-2.5" /> K
            </div>
          </button>
        </div>

        <div className="flex items-center gap-6">
          <div className="hidden lg:block max-w-48 text-right">
            <p className="text-xs font-black text-slate-700 dark:text-slate-200 truncate" title={tenantName}>{tenantName}</p>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Organisasjon</p>
          </div>

          <button
            onClick={toggleDarkMode}
            className="p-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl transition-all"
            title={darkMode ? 'Bytt til lys modus' : 'Bytt til natt modus'}
          >
            <SafeIcon icon={darkMode ? "☀️" : "🌙"} className="w-5 h-5" />
          </button>

          <button
            onClick={toggleHelp}
            className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 rounded-lg text-xs font-bold hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-all border border-indigo-100 dark:border-indigo-800"
          >
            <SafeIcon icon="💡" className="w-4 h-4 animate-pulse" />
            <span className="hidden md:inline">Hjelp for denne siden</span>
          </button>

          <div className="relative">
            <button className="p-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl transition-all relative">
              <SafeIcon icon="🔔" className="w-5 h-5" />
              {notifications.length > 0 && (
                <span className="absolute top-2 right-2 w-2 h-2 bg-rose-500 rounded-full border-2 border-white dark:border-slate-900" />
              )}
            </button>
          </div>

          {permissions.canSettings && (
            <button onClick={() => navigate('/settings')} className="p-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl transition-all">
              <SafeIcon icon="⚙️" className="w-5 h-5" />
            </button>
          )}

          <div className="h-8 w-px bg-slate-100 dark:bg-slate-800 mx-2" />

          <div className="flex items-center gap-3 pl-2">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-black text-slate-900 dark:text-white leading-none">{currentUser.name}</p>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">{permissions.role}</p>
            </div>
            <div className="w-10 h-10 rounded-xl overflow-hidden border-2 border-white dark:border-slate-700 shadow-sm">
              <img src={currentUser.avatar} alt={currentUser.name} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>

      </header>

      <CommandPalette isOpen={isPaletteOpen} onClose={() => setIsPaletteOpen(false)} />
    </>
  );
};

export default Header;